/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router'
import { filter, lastValueFrom } from 'rxjs'
import { take } from 'rxjs/operators'
import { ConfigService } from '../config/config.service'
import { CookieConsentImplementationService } from '../cookie-consent/cookie-consent.service'
import { LocaleRetrieveUrlParamsSignupService } from '../config/locale-retrieve-url-params-signup.service'
import { LocaleHandlingService } from '../locale-handling/locale-handling.service'
import { NewRelicService } from '../new-relic/new-relic-service'

@Injectable({
  providedIn: 'root',
})
export class SetupGuardService {
  private configService = inject(ConfigService)
  private localeRetrieveUrlParamsService = inject(LocaleRetrieveUrlParamsSignupService)
  private router = inject(Router)
  private cookieConsentBackendService = inject(CookieConsentImplementationService)
  private localeHandlingService = inject(LocaleHandlingService)
  private newRelicService = inject(NewRelicService)
  private alreadyInitialized = false

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (!this.alreadyInitialized) {
      this.alreadyInitialized = true
      const params = this.localeRetrieveUrlParamsService.retrieve(route)
      this.configService.setUrlLocaleCountryInformation(params.locale, params.country)

      await lastValueFrom(this.configService.init(params.locale, params.country))
      const localeHandlingSuccess = await this.localeHandlingService.setup()
      this.newRelicService.addPageAction(params.country)

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe(async () => {
          await this.cookieConsentBackendService.init()
        })
      return localeHandlingSuccess
    }

    return true
  }
}
